// Grit's database client. Grit moved off Supabase to Cloudflare D1 on 2026-10-15
// (the old project was deleted in August 2026). This keeps the supabase-js chain
// shape the data layer already uses, sb.from(t).select().eq()..., and sends each
// finished query to functions/api/db.js, which builds the SQL and enforces
// "read anything, write only your own rows."
//
// Supported: select (with "alias:target(cols)" embeds), insert, update, upsert,
// delete; filters eq/neq/in/is(null)/not(col,'is',null); order; limit; single;
// maybeSingle. A query runs when awaited and resolves to { data, error }.
//
// Bad gym signal (plan v4 item 4):
//  - Every read that succeeds is kept on the phone; when the network fails, the last
//    copy is returned instead (so a loaded workout or run plan opens without signal).
//  - Workout and run saves that hit a network failure wait in an outbox on the phone
//    and are sent, in order, when signal returns. They resolve as saved-on-phone
//    ({ pending: true }) and isPending(id) / pendingCount() say so honestly.
//  - Inserts carry an id made on the phone, and the server ignores a repeat of the
//    same id, so a resend never makes a duplicate. Food is NOT queued (plan v4).

const API = '/api/db';
const CACHE_PREFIX = 'grit-cache:';
const CACHE_INDEX = 'grit-cache-index';
const CACHE_MAX = 200;
const OUTBOX = 'grit-outbox';
// Tables whose rows have an "id" primary key the phone can fill in.
const ID_TABLES = new Set(['grit_programs', 'grit_program_days', 'grit_program_exercises', 'grit_sessions', 'grit_sets', 'grit_runs', 'grit_run_plans', 'grit_goals', 'grit_push_subscriptions', 'grit_exercise_notes', 'grit_weekly_goals', 'grit_goal_checkins', 'grit_food_log', 'grit_body_weight', 'grit_exercises']);
// Saves that may wait on the phone (the gym ones). Food and body data stay online-only.
const QUEUE_TABLES = new Set(['grit_sessions', 'grit_sets', 'grit_runs', 'grit_program_exercises', 'grit_run_plans', 'grit_settings', 'grit_goals', 'grit_exercise_notes']);

// Active grit user ("kenny" | "cassidy"), set by auth.js. Sent on every request
// so the server knows whose rows a write may touch.
export function gritUser() {
  if (typeof localStorage === 'undefined') return ''; // safe under tests
  return localStorage.getItem('grit-user') || '';
}

// Kept for auth.js: the header is read fresh on every request now.
export function refreshUserHeader() {}

const store = {
  get(k) { try { return JSON.parse(localStorage.getItem(k)); } catch { return null; } },
  set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); return true; } catch { return false; } },
  del(k) { try { localStorage.removeItem(k); } catch { /* ignore */ } },
};

function hash(str) { let h = 5381; for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) | 0; return (h >>> 0).toString(36); }
function cacheKey(q, user) { return CACHE_PREFIX + hash(user + '|' + JSON.stringify(q)); }
function cachePut(key, data) {
  if (!store.set(key, data)) return;
  const idx = (store.get(CACHE_INDEX) || []).filter((k) => k !== key);
  idx.push(key);
  while (idx.length > CACHE_MAX) store.del(idx.shift());
  store.set(CACHE_INDEX, idx);
}

function uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'x' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
}

// ── Outbox ────────────────────────────────────────────────────────────────────
function outbox() { return store.get(OUTBOX) || []; }
function setOutbox(list) { store.set(OUTBOX, list); notify(); }
function notify() { if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('grit-outbox', { detail: { count: outbox().length } })); }
export function pendingCount() { return outbox().length; }
export function isPending(id) { return !!id && outbox().some((o) => o.ids?.includes(id)); }

let flushing = false;
export async function flushOutbox() {
  if (flushing) return;
  flushing = true;
  try {
    let list = outbox();
    while (list.length) {
      const item = list[0];
      let res;
      try {
        res = await fetch(API, { method: 'POST', headers: { 'content-type': 'application/json', 'x-grit-user': item.user }, body: JSON.stringify(item.q) });
      } catch { break; } // still no signal: try again later
      if (res.status >= 500) break;
      const body = await res.json().catch(() => null);
      // A server refusal will never succeed on retry; set it aside so it can't block the rest.
      if (body?.error) { const failed = store.get('grit-outbox-failed') || []; failed.push({ ...item, error: body.error }); store.set('grit-outbox-failed', failed.slice(-50)); }
      list = outbox().slice(1);
      setOutbox(list);
    }
  } finally {
    flushing = false;
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => flushOutbox());
  setInterval(() => { if (outbox().length) flushOutbox(); }, 20000);
  setTimeout(() => { if (outbox().length) flushOutbox(); }, 1500);
}

// ── Query builder ─────────────────────────────────────────────────────────────
class Query {
  constructor(table) {
    this.q = { table, op: 'select', columns: '*', filters: [], order: [] };
  }
  select(columns = '*') {
    this.q.columns = columns;
    if (this.q.op !== 'select') this.q.returning = true;
    return this;
  }
  insert(values) { this.q.op = 'insert'; this.q.values = withIds(this.q.table, values); this.q.returning = false; return this; }
  update(values) { this.q.op = 'update'; this.q.values = values; this.q.returning = false; return this; }
  upsert(values, { onConflict } = {}) { this.q.op = 'upsert'; this.q.values = withIds(this.q.table, values); this.q.onConflict = onConflict; this.q.returning = false; return this; }
  delete() { this.q.op = 'delete'; return this; }
  eq(col, val) { this.q.filters.push([col, 'eq', val]); return this; }
  neq(col, val) { this.q.filters.push([col, 'neq', val]); return this; }
  in(col, vals) { this.q.filters.push([col, 'in', vals]); return this; }
  is(col, val) { this.q.filters.push([col, 'is', val]); return this; }
  not(col, op, val) {
    if (op === 'is' && val === null) { this.q.filters.push([col, 'notis', null]); return this; }
    throw new Error(`not(${op}) is not supported`);
  }
  order(col, { ascending = true } = {}) { this.q.order.push([col, ascending]); return this; }
  limit(n) { this.q.limit = n; return this; }
  single() { this.q.single = 'one'; return this; }
  maybeSingle() { this.q.single = 'maybe'; return this; }

  async run() {
    const user = gritUser();
    const isRead = this.q.op === 'select';
    let res;
    try {
      // A gym's weak signal can hang a request; give up after 8 s and use the phone.
      const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
      const timer = ctl ? setTimeout(() => ctl.abort(), 8000) : null;
      res = await fetch(API, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'x-grit-user': user },
        body: JSON.stringify(this.q),
        signal: ctl?.signal,
      });
      if (timer) clearTimeout(timer);
      if (res.status >= 500) throw new Error(`HTTP ${res.status}`);
    } catch (e) {
      return this.offline(user, isRead, e);
    }
    const body = await res.json().catch(() => null);
    if (!body) return { data: null, error: { message: `HTTP ${res.status}` } };
    if (isRead && !body.error) cachePut(cacheKey(this.q, user), body.data);
    if (outbox().length) flushOutbox(); // signal is back: send anything waiting
    return body;
  }

  // No network: reads use the last copy on the phone; gym saves wait in the outbox.
  offline(user, isRead, e) {
    const message = String(e?.message || e);
    if (isRead) {
      const cached = store.get(cacheKey(this.q, user));
      return cached !== null ? { data: cached, error: null, fromCache: true } : { data: null, error: { message, offline: true } };
    }
    if (!QUEUE_TABLES.has(this.q.table)) return { data: null, error: { message, offline: true } };
    const rows = Array.isArray(this.q.values) ? this.q.values : (this.q.values ? [this.q.values] : []);
    const ids = rows.map((r) => r?.id).filter(Boolean);
    const list = outbox();
    list.push({ q: this.q, user, ids, at: Date.now() });
    if (!store.set(OUTBOX, list)) return { data: null, error: { message: 'Phone storage is full', offline: true } };
    notify();
    let data = null;
    if (this.q.returning !== false && (this.q.op === 'insert' || this.q.op === 'upsert')) data = this.q.single ? rows[0] : rows;
    return { data, error: null, pending: true };
  }

  then(resolve, reject) { return this.run().then(resolve, reject); }
}

function withIds(table, values) {
  if (!ID_TABLES.has(table)) return values;
  const add = (r) => (r && !r.id ? { ...r, id: uuid() } : r);
  return Array.isArray(values) ? values.map(add) : add(values);
}

export const sb = { from: (table) => new Query(table) };
